<template>
  <div class="score-diagnosis">
    <!-- Banner -->
    <div class="banner">
      <div class="banner-content">
        <img class="logo" :src="bannerImage" alt="优益优" />
      </div>
    </div>

    <!-- Subject Direction -->
    <div class="subject-direction">
      <div class="direction-label">
        学科方向
        <span class="separator">|</span>
      </div>
      <van-radio-group
        v-model="direction"
        direction="horizontal"
        class="direction-options"
      >
        <van-radio
          v-for="(item, index) in directionOptions"
          :key="index"
          :name="item.value"
          icon-size="16px"
          >{{ item.label }}</van-radio
        >
      </van-radio-group>
    </div>

    <!-- Diagnosis Tests -->
    <div v-for="test in diagnosisModal" :key="test.key" class="score-card">
      <div class="exam-header">
        <div class="title-wrapper">
          <div class="title-section">
            <img :src="examIcon" alt="" class="exam-icon" />
            <span>{{ test.title }}</span>
          </div>
          <div class="subtitle-section">
            <span>{{ test.subtitle }}</span>
          </div>
        </div>
      </div>
      <div class="score-grid">
        <div
          class="score-item"
          v-for="subject in test.subjects"
          :key="subject.key"
        >
          <span class="subject">{{ subject.label }}</span>
          <van-field
            v-model="test.scores[subject.key]"
            type="digit"
            :placeholder="subject.key == 'composite' ? '0-300' : '0-150'"
            :border="false"
            :formatter="
              subject.key == 'composite' ? formatterComposite : formatter
            "
            class="score-input"
          />
        </div>
      </div>
    </div>

    <!-- Submit Button -->
    <div class="submit-button">
      <van-button type="primary" block round @click="onAnalyze"
        >立即分析</van-button
      >
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import {
  Icon as VanIcon,
  Field as VanField,
  Button as VanButton,
  Radio as VanRadio,
  RadioGroup as VanRadioGroup,
  showToast,
} from "vant";
import logoWhiteImage from "../../assets/course/logo-white.png";
import examIcon from "../../assets/course/exam-icon.gif";
import { querySurveySelectSurveyById } from "@/api/user";
import { useRoute, useRouter } from "vue-router";

const direction = ref(null);
const directionOptions = ref([]);
const directionKey = ref(null);

const bannerImage = ref(null);

const surveyId = ref(null);

const orgCode = ref(null)

const phoneNum = ref(null)

const route = useRoute();

const router = useRouter();

// 诊断考试配置
const diagnosisModal = ref([]);

onMounted(() => {
  surveyId.value = route?.query?.surveyId;
  fetchDataList();
});

function convertContentDesc(contentDesc) {
  // 定义科目固定顺序及中英文键映射
  const fixedSubjects = [
    { label: "语文", key: "chinese" },
    { label: "数学", key: "math" },
    { label: "英语", key: "english" },
    { label: "综合", key: "composite" },
  ];

  // 处理并排序诊断考试项
  const exams = contentDesc
    .filter((item) => item.questionType === 8) // 筛选诊断考试
    .map((item) => {
      const numChar = (item.questionTitle.match(/第(.)次/) || [])[1] || "";
      return {
        ...item,
        numOrder: { 一: 1, 二: 2 }[numChar] || Infinity, // 确定排序顺序
        numChar,
      };
    })
    .sort((a, b) => a.numOrder - b.numOrder); // 按第一次、第二次排序

  // 转换为目标结构
  return exams.map((exam) => {
    const key = exam.numChar === "一" ? "first" : "second";
    const title = exam.questionTitle.replace(/考试$/, "考");
    const subtitle = `请输入您的${exam.numChar}诊考试分数`;
    const id = exam.id;

    // 解析选项并生成固定顺序的科目
    const options = JSON.parse(exam.options);
    const optionsValues = new Map()
    options.forEach(item => {
      optionsValues.set(item.label, item.value)
    })

    console.log(optionsValues)
    
    const optionLabels = new Set(options.map((opt) => opt.label));
    const subjects = fixedSubjects
      .filter((subj) => optionLabels.has(subj.label))
      .map(({ key, label }) => ({ key, label, value: optionsValues.get(label) }));

    console.log(subjects)

    // 生成分数对象
    const scores = subjects.reduce((acc, { key }) => {
      acc[key] = "";
      return acc;
    }, {});

    console.log(scores)

    return { key, title, subtitle, subjects, scores, id };
  });
}

const fetchDataList = async () => {
  try {
    const result = await querySurveySelectSurveyById({
      id: surveyId.value,
    });
    if (result.code == "00") {
      bannerImage.value = result.data.bannerUrl;
      document.title = result.data.title;
      orgCode.value = result.data.createOrg
      phoneNum.value = result.data.phoneNum
      const directionInfo = result.data.contentDesc.filter(
        (item) => item.questionType == 1
      )?.[0];

      directionOptions.value = JSON.parse(directionInfo.options);
      directionKey.value = directionInfo.id;

      diagnosisModal.value = convertContentDesc(result.data.contentDesc);
    }
  } catch (error) {
    console.log(error);
  }
};

const formatter = (value) => {
  // 限制只能输入数字
  value = value.replace(/[^0-9]/g, "");
  // 限制最大值为150
  if (value > 150) value = "150";
  return value;
};

const formatterComposite = (value) => {
  // 限制只能输入数字
  value = value.replace(/[^0-9]/g, "");
  // 限制最大值为150
  if (value > 300) value = "300";
  return value;
};

const onAnalyze = () => {
  // 验证所有字段是否填写

  if (!direction.value) {
    showToast("请选择学科方向");
    return;
  }

  const hasEmptyScore = diagnosisModal.value.some((test) =>
    Object.values(test.scores).some((score) => !score)
  );

  if (hasEmptyScore) {
    showToast("请填写完整的成绩信息");
    return;
  }

  const options = directionOptions.value;
  const _direction = options.filter(item => item.value == direction.value)?.[0].label
  router.push({
    path: "/course-selection",
    query: {
      surveyId: surveyId.value,
      direction: _direction,
      directionValue: direction.value,
      directionKey: directionKey.value,
      info: JSON.stringify(diagnosisModal.value),
      orgCode: orgCode.value,
      phoneNum: phoneNum.value
    },
  });
};
</script>

<style scoped>
.score-diagnosis {
  min-height: 100vh;
  background-color: #f2f2f2;
  overflow-y: scroll;
}

.banner {
  width: 100%;
  background: #fff;
  position: relative;
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;
  margin-top: 5px;
}

.banner-content {
  position: relative;
  z-index: 1;
}

.logo {
  width: calc(100% - 24px);
  padding: 12px;
  height: auto;
}

.banner-text {
  margin-top: 0;
}

.banner-text .title {
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  line-height: 1.4;
  letter-spacing: 1px;
}

.banner-text .subtitle {
  font-size: 14px;
  margin: 8px 0 0;
  opacity: 0.8;
  line-height: 1.4;
}

.mascot {
  position: absolute;
  right: 20px;
  bottom: 0;
  width: 140px;
  height: auto;
  z-index: 1;
}

.subject-direction {
  margin: 12px;
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
}

.direction-label {
  font-size: 15px;
  color: #323233;
  display: flex;
  align-items: center;
}

.separator {
  color: #dcdcdc;
  margin: 0 12px;
}

.direction-options {
  flex: 1;
}

.direction-options :deep(.van-radio) {
  margin-right: 24px;
}

.direction-options :deep(.van-radio__label) {
  color: #323233;
  font-size: 15px;
}

.score-card {
  margin: 12px;
  background: #fff;
  border-radius: 8px;
  padding: 12px;
}

.exam-header {
  margin-bottom: 20px;
}

.title-wrapper {
  display: inline-flex;
  align-items: stretch;
}

.title-section {
  display: flex;
  align-items: center;
  background: #5a68ea;
  padding: 8px 0px 8px 8px;
  position: relative;
  color: #fff;
  border-radius: 8px;
}

.title-section::after {
  content: "";
  position: absolute;
  right: -20px;
  top: 0;
  width: 40px;
  height: 100%;
  background: #5a68ea;
  transform: skewX(-15deg);
}

.exam-icon {
  width: 20px;
  height: 20px;
  margin-right: 8px;
  position: relative;
  z-index: 1;
}

.title-section span {
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  position: relative;
  z-index: 1;
  white-space: nowrap;
}

.subtitle-section {
  display: flex;
  align-items: center;
  background: #eef0ff;
  padding: 8px 24px 8px 8px;
  border-radius: 8px;
  margin-left: 32px;
  position: relative;
}

.subtitle-section::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 24px;
  height: 100%;
  background: inherit;
  transform: skewX(-15deg);
  transform-origin: left top;
}

.subtitle-section span {
  color: #5a68ea;
  font-size: 14px;
  line-height: 20px;
  position: relative;
  z-index: 1;
  white-space: nowrap;
}

.score-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
}

.score-item {
  flex: 1;
  min-width: calc(50% - 6px);
  max-width: 100%;
  display: flex;
  align-items: center;
}

.subject {
  font-size: 14px;
  color: #333;
  margin-right: 8px;
  width: 28px;
}

:deep(.score-input) {
  flex: 1;
}

:deep(.score-input .van-field__control) {
  height: 32px;
  padding: 0 12px;
  font-size: 14px;
  color: #333;
  text-align: left;
  background: #f5f6fa;
  border: 1px solid #ebedf0;
  border-radius: 4px;
}

:deep(.score-input .van-field__placeholder) {
  color: #999;
  font-size: 14px;
}

:deep(.van-field__body) {
  background: transparent;
}

.submit-button {
  margin: 24px 12px;
}

:deep(.van-button--primary) {
  background: #5a68ea;
  border-color: #5a68ea;
  height: 44px;
  font-size: 16px;
}
</style> 